import {
  Goal,
  goalsArchivedSectionID,
  ID,
  LifeArea,
  lifeAreaOtherID,
  rootId,
  TaskListItemTypes,
  TaskListTreeItem,
} from '@griply/types';

import {
  getArchivedGoalIds,
  getCompletedGoalIds,
  getInheritedLifeAreaIds,
} from '../../derived-goal-state';
import { getNearestPresentParentId } from '../get-nearest-present-parent-id';
import { BuildGoalListTreeOptions } from './types';

export type BuildLifeAreaGoalListTreeOptions = BuildGoalListTreeOptions & {
  otherLifeAreaName: string;
};

export const buildLifeAreaGoalListTree = (
  goals: Goal[],
  lifeAreas: LifeArea[],
  {
    hideArchived,
    hideCompleted,
    archivedSectionName,
    otherLifeAreaName,
    allGoals,
  }: BuildLifeAreaGoalListTreeOptions,
): Record<ID, TaskListTreeItem> => {
  const archivedIds = getArchivedGoalIds(allGoals);
  const completedIds = getCompletedGoalIds(allGoals);
  const lifeAreaIds = getInheritedLifeAreaIds(allGoals);
  const activeGoals: Goal[] = [];
  const archivedGoals: ID[] = [];

  goals.forEach((goal) => {
    if (hideArchived && archivedIds.has(goal.id)) {
      return;
    }
    if (goal.archivedAt) {
      archivedGoals.push(goal.id);
      return;
    }
    if (!hideCompleted || !completedIds.has(goal.id)) {
      activeGoals.push(goal);
    }
  });

  const treeItems: Record<ID, TaskListTreeItem> = {};

  lifeAreas.forEach((lifeArea) => {
    treeItems[lifeArea.id] = {
      id: lifeArea.id,
      type: TaskListItemTypes.LifeArea,
      name: lifeArea.name,
      children: [],
      isFolder: true,
    };
  });

  treeItems[lifeAreaOtherID] = {
    id: lifeAreaOtherID,
    type: TaskListItemTypes.LifeArea,
    name: otherLifeAreaName,
    children: [],
    isFolder: true,
  };

  goals.forEach((goal) => {
    treeItems[goal.id] = {
      id: goal.id,
      type: TaskListItemTypes.Goal,
      name: goal.name,
      children: [],
      isFolder: true,
    };
  });

  activeGoals.forEach((goal) => {
    const parentId = getNearestPresentParentId(goal, (id) => !!treeItems[id]);

    if (parentId) {
      treeItems[parentId].children?.push(goal.id);
      return;
    }

    // Archived through an ancestor the filter dropped — no parent left to nest under.
    if (archivedIds.has(goal.id)) {
      archivedGoals.push(goal.id);
      return;
    }

    const lifeAreaId = lifeAreaIds.get(goal.id) ?? lifeAreaOtherID;
    const targetId = treeItems[lifeAreaId] ? lifeAreaId : lifeAreaOtherID;
    treeItems[targetId].children?.push(goal.id);
  });

  if (archivedGoals.length) {
    treeItems[goalsArchivedSectionID] = {
      id: goalsArchivedSectionID,
      type: TaskListItemTypes.LifeArea,
      name: archivedSectionName,
      children: archivedGoals,
      isFolder: true,
    };
  }

  const rootChildren = lifeAreas.reduce<ID[]>((acc, lifeArea) => {
    if (treeItems[lifeArea.id].children?.length) {
      acc.push(lifeArea.id);
    }
    return acc;
  }, []);

  if (treeItems[lifeAreaOtherID]?.children?.length) {
    rootChildren.push(lifeAreaOtherID);
  }

  if (archivedGoals.length) {
    rootChildren.push(goalsArchivedSectionID);
  }

  return {
    [rootId]: {
      id: rootId,
      name: 'Root',
      type: TaskListItemTypes.Root,
      children: rootChildren,
    },
    ...treeItems,
  };
};
